/**
 * Turn page drawings into one SVG per figure, then attach each figure to a question.
 *
 * A test passes hand-written drawings. It does not need a PDF or pdf.js.
 * The PDF reader is the only producer of these drawings.
 *
 * @format
 */

/** A rectangle in PDF user space. The origin is the bottom-left of the page. */
export interface FigureBox {
	x: number;
	y: number;
	width: number;
	height: number;
}

export type PathCommand =
	| { op: "M"; x: number; y: number }
	| { op: "L"; x: number; y: number }
	| { op: "C"; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
	| { op: "Z" };

export interface VectorDrawing {
	kind: "path";
	box: FigureBox;
	commands: PathCommand[];
	stroke?: string;
	fill?: string;
	fillRule?: "nonzero" | "evenodd";
	lineWidth: number;
}

export interface ImageDrawing {
	kind: "image";
	box: FigureBox;
	/** Pixel width of the embedded image, before the page scales it. */
	intrinsicWidth: number;
	/** Pixel height of the embedded image, before the page scales it. */
	intrinsicHeight: number;
	href: string;
}

export type PageDrawing = VectorDrawing | ImageDrawing;

export interface FigurePage {
	pageNumber: number;
	pageWidth: number;
	pageHeight: number;
	drawings: PageDrawing[];
}

export interface ClusteredFigure {
	page: number;
	x: number;
	y: number;
	width: number;
	height: number;
	svg: string;
}

export interface FigureLabel {
	questionNumber: string;
	page: number;
	/** Baseline of the label. Null when the page is known and the y is not. */
	y: number | null;
}

export interface AssetRef {
	en?: string;
	cy?: string;
}

export interface AssetManifest {
	questions: {
		questionNumber: string;
		paper: AssetRef[];
		markScheme: AssetRef[];
	}[];
}

const HAIRLINE = 3;
const SEED_SHORT = 36;
const SEED_LONG = 72;
const CLUSTER_GAP = 12;
const BACKGROUND_SHARE = 0.92;
const IMAGE_MIN_PX = 200;
const MAX_PIXELS = 8_000_000;
const LABEL_SLACK = 8;
/**
 * How far a label may sit from an image and still belong to it, in PDF points.
 * One caption line fits. A question line further out stays out.
 */
export const ASSET_NEAR_MARGIN = 32;

/** Knobs for the figure crop. Omit them and the code defaults apply. */
export interface FigureClusterTune {
	hairline: number;
	seedShort: number;
	seedLong: number;
	clusterGap: number;
	imageMinPx: number;
	nearMargin: number;
}

export const DEFAULT_FIGURE_CLUSTER_TUNE: FigureClusterTune = {
	hairline: HAIRLINE,
	seedShort: SEED_SHORT,
	seedLong: SEED_LONG,
	clusterGap: CLUSTER_GAP,
	imageMinPx: IMAGE_MIN_PX,
	nearMargin: ASSET_NEAR_MARGIN,
};
/** A caption may sit just above the drawing it names, or a short way to its left. */
const CAPTION_SLACK = 120;
const CAPTION_X_BEFORE = 100;
const CAPTION_X_AFTER = 24;

/** Same join key as the mark-scheme extractor. A space or a trailing dot does not change the number. */
const normaliseAssetNumber = (value: string): string =>
	value
		.toLowerCase()
		.replace(/^q(?:uestion)?\s*/, "")
		.replace(/(\d)\s*[.)]/g, "$1")
		.replace(/[\s.]/g, "");

const formatNumber = (value: number): string => {
	const rounded = Math.round(value * 100) / 100;
	if (Object.is(rounded, -0) || Object.is(rounded, 0)) {
		return "0";
	}
	return String(rounded);
};

const escapeAttr = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

const isSafeHref = (href: string): boolean =>
	href.startsWith("data:image/png;base64,") || href.startsWith("data:image/jpeg;base64,");

const unionBox = (left: FigureBox, right: FigureBox): FigureBox => {
	const x = Math.min(left.x, right.x);
	const y = Math.min(left.y, right.y);
	const maxX = Math.max(left.x + left.width, right.x + right.width);
	const maxY = Math.max(left.y + left.height, right.y + right.height);
	return { x, y, width: maxX - x, height: maxY - y };
};

const overlapsBox = (left: FigureBox, right: FigureBox): boolean =>
	left.x < right.x + right.width &&
	right.x < left.x + left.width &&
	left.y < right.y + right.height &&
	right.y < left.y + left.height;

const withinGap = (left: FigureBox, right: FigureBox, clusterGap: number): boolean => {
	const isSeparatedX = left.x + left.width + clusterGap < right.x || right.x + right.width + clusterGap < left.x;
	const isSeparatedY = left.y + left.height + clusterGap < right.y || right.y + right.height + clusterGap < left.y;
	return !isSeparatedX && !isSeparatedY;
};

const isBackground = (box: FigureBox, page: FigurePage): boolean =>
	box.width > page.pageWidth * BACKGROUND_SHARE && box.height > page.pageHeight * BACKGROUND_SHARE;

/** The ruled border around a WJEC page. It is not a diagram. */
const isPageFrame = (drawing: PageDrawing, page: FigurePage): boolean => {
	if (drawing.kind !== "path" || drawing.lineWidth > 1) {
		return false;
	}
	const right = page.pageWidth - (drawing.box.x + drawing.box.width);
	const top = page.pageHeight - (drawing.box.y + drawing.box.height);
	return (
		drawing.box.x < 100 &&
		drawing.box.y < 120 &&
		right < 100 &&
		top < 120 &&
		drawing.box.width > page.pageWidth * 0.7 &&
		drawing.box.height > page.pageHeight * 0.7
	);
};

const isHairline = (box: FigureBox, hairline: number): boolean => Math.min(box.width, box.height) <= hairline;

const isFigureSized = (box: FigureBox, tune: FigureClusterTune): boolean => {
	const shortSide = Math.min(box.width, box.height);
	const longSide = Math.max(box.width, box.height);
	return shortSide >= tune.seedShort && longSide >= tune.seedLong;
};

const isImageSeed = (drawing: ImageDrawing, tune: FigureClusterTune): boolean => {
	if (drawing.intrinsicWidth < tune.imageMinPx || drawing.intrinsicHeight < tune.imageMinPx) {
		return false;
	}
	if (drawing.intrinsicWidth * drawing.intrinsicHeight > MAX_PIXELS) {
		return false;
	}
	if (isHairline(drawing.box, tune.hairline) || !isSafeHref(drawing.href)) {
		return false;
	}
	return true;
};

const svgPoint = (figure: FigureBox, x: number, y: number): string => {
	const svgX = x - figure.x;
	const svgY = figure.y + figure.height - y;
	return `${formatNumber(svgX)} ${formatNumber(svgY)}`;
};

const commandToPath = (figure: FigureBox, command: PathCommand): string => {
	if (command.op === "M" || command.op === "L") {
		return `${command.op} ${svgPoint(figure, command.x, command.y)}`;
	}
	if (command.op === "Z") {
		return "Z";
	}
	return `C ${svgPoint(figure, command.x1, command.y1)} ${svgPoint(figure, command.x2, command.y2)} ${svgPoint(figure, command.x, command.y)}`;
};

const drawingElement = (figure: FigureBox, drawing: PageDrawing): string | undefined => {
	if (drawing.kind === "image") {
		if (!isSafeHref(drawing.href)) {
			return undefined;
		}
		const x = drawing.box.x - figure.x;
		const y = figure.y + figure.height - (drawing.box.y + drawing.box.height);
		return `<image href="${escapeAttr(drawing.href)}" x="${formatNumber(x)}" y="${formatNumber(y)}" width="${formatNumber(drawing.box.width)}" height="${formatNumber(drawing.box.height)}"/>`;
	}
	const path = drawing.commands.map((command) => commandToPath(figure, command)).join(" ");
	if (path.trim() === "") {
		return undefined;
	}
	const fill = drawing.fill ? escapeAttr(drawing.fill) : "none";
	const stroke = drawing.stroke ? escapeAttr(drawing.stroke) : "none";
	const rule = drawing.fillRule === "evenodd" ? ` fill-rule="evenodd"` : "";
	const width = drawing.lineWidth > 0 ? drawing.lineWidth : 1;
	return `<path d="${escapeAttr(path)}" fill="${fill}" stroke="${stroke}" stroke-width="${formatNumber(width)}"${rule}/>`;
};

const svgFor = (drawings: readonly PageDrawing[]): string | undefined => {
	if (drawings.length === 0) {
		return undefined;
	}
	let box = drawings[0].box;
	for (const drawing of drawings.slice(1)) {
		box = unionBox(box, drawing.box);
	}
	if (box.width <= 0 || box.height <= 0) {
		return undefined;
	}
	const body = drawings
		.map((drawing) => drawingElement(box, drawing))
		.filter((element): element is string => element !== undefined)
		.join("");
	if (body.length === 0) {
		return undefined;
	}
	const width = formatNumber(box.width);
	const height = formatNumber(box.height);
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">${body}</svg>`;
};

const clusterDrawings = (seeds: PageDrawing[], rules: PageDrawing[], clusterGap: number): PageDrawing[][] => {
	const parent = seeds.map((_, index) => index);
	const find = (index: number): number => {
		let cursor = index;
		while (parent[cursor] !== cursor) {
			cursor = parent[cursor];
		}
		return cursor;
	};
	for (let left = 0; left < seeds.length; left += 1) {
		for (let right = left + 1; right < seeds.length; right += 1) {
			if (!withinGap(seeds[left].box, seeds[right].box, clusterGap)) {
				continue;
			}
			const leftRoot = find(left);
			const rightRoot = find(right);
			if (leftRoot !== rightRoot) {
				parent[rightRoot] = leftRoot;
			}
		}
	}
	const groups = new Map<number, PageDrawing[]>();
	for (let index = 0; index < seeds.length; index += 1) {
		const root = find(index);
		const group = groups.get(root) ?? [];
		group.push(seeds[index]);
		groups.set(root, group);
	}
	for (const group of groups.values()) {
		const seedBox = group.reduce((union, drawing) => unionBox(union, drawing.box), group[0].box);
		for (const rule of rules) {
			if (withinGap(seedBox, rule.box, clusterGap)) {
				group.push(rule);
			}
		}
	}
	return [...groups.values()];
};

const expandBox = (box: FigureBox, nearMargin: number): FigureBox => ({
	x: box.x - nearMargin,
	y: box.y - nearMargin,
	width: box.width + nearMargin * 2,
	height: box.height + nearMargin * 2,
});

const boxGap = (left: FigureBox, right: FigureBox): number => {
	const gapX =
		left.x > right.x + right.width
			? left.x - (right.x + right.width)
			: right.x > left.x + left.width
				? right.x - (left.x + left.width)
				: 0;
	const gapY =
		left.y > right.y + right.height
			? left.y - (right.y + right.height)
			: right.y > left.y + left.height
				? right.y - (left.y + left.height)
				: 0;
	return gapX * gapX + gapY * gapY;
};

const nearestImage = (
	drawing: PageDrawing,
	images: readonly ImageDrawing[],
	nearMargin: number,
): ImageDrawing | undefined => {
	let chosen: ImageDrawing | undefined;
	let best = Number.POSITIVE_INFINITY;
	for (const image of images) {
		if (!overlapsBox(drawing.box, expandBox(image.box, nearMargin))) {
			continue;
		}
		const gap = boxGap(drawing.box, image.box);
		if (gap < best) {
			best = gap;
			chosen = image;
		}
	}
	return chosen;
};

/**
 * Paths within {@link ASSET_NEAR_MARGIN} of an image join that image.
 * A path further away stays out, even when a nearer path bridges the gap.
 * A page with no image keeps the clustered groups.
 */
const attachLabelsNearImages = (
	groups: PageDrawing[][],
	pool: readonly PageDrawing[],
	nearMargin: number,
): PageDrawing[][] => {
	const images = pool.filter((drawing): drawing is ImageDrawing => drawing.kind === "image");
	if (images.length === 0) {
		return groups;
	}
	const owner = new Map<ImageDrawing, PageDrawing[]>();
	for (const image of images) {
		owner.set(image, [image]);
	}
	const consumed = new Set<PageDrawing>(images);
	for (const drawing of pool) {
		if (drawing.kind === "image") {
			continue;
		}
		const image = nearestImage(drawing, images, nearMargin);
		if (!image) {
			continue;
		}
		owner.get(image)?.push(drawing);
		consumed.add(drawing);
	}
	const merged = groups
		.filter((group) => group.some((drawing) => drawing.kind === "image"))
		.map((group) => {
			const seen = new Set<PageDrawing>();
			const combined: PageDrawing[] = [];
			for (const drawing of group) {
				if (drawing.kind !== "image") {
					continue;
				}
				for (const item of owner.get(drawing) ?? []) {
					if (!seen.has(item)) {
						seen.add(item);
						combined.push(item);
					}
				}
			}
			return combined;
		});
	const rest = groups
		.filter((group) => group.every((drawing) => drawing.kind !== "image"))
		.map((group) => group.filter((drawing) => !consumed.has(drawing)))
		.filter((group) => group.length > 0);
	return [...merged, ...rest];
};

/** Keep a real figure. Drop answer lines, page backgrounds, and bitmaps under the minimum size. */
export const clusterFigurePage = (
	page: FigurePage,
	tune: FigureClusterTune = DEFAULT_FIGURE_CLUSTER_TUNE,
): ClusteredFigure[] => {
	const seeds: PageDrawing[] = [];
	const rules: PageDrawing[] = [];
	for (const drawing of page.drawings) {
		if (isBackground(drawing.box, page) || isPageFrame(drawing, page)) {
			continue;
		}
		if (drawing.kind === "image") {
			if (isImageSeed(drawing, tune)) {
				seeds.push(drawing);
			}
			continue;
		}
		if (isHairline(drawing.box, tune.hairline)) {
			rules.push(drawing);
			continue;
		}
		seeds.push(drawing);
	}
	const figures: ClusteredFigure[] = [];
	for (const group of attachLabelsNearImages(
		clusterDrawings(seeds, rules, tune.clusterGap),
		[...seeds, ...rules],
		tune.nearMargin,
	)) {
		const svg = svgFor(group);
		const box = group.reduce((union, drawing) => unionBox(union, drawing.box), group[0].box);
		if (!svg || !isFigureSized(box, tune)) {
			continue;
		}
		figures.push({ page: page.pageNumber, x: box.x, y: box.y, width: box.width, height: box.height, svg });
	}
	return figures.sort((left, right) => right.y - left.y || left.x - right.x);
};

const questionNumbersFor = (figure: ClusteredFigure, labels: readonly FigureLabel[]): string[] => {
	const onPage = labels.filter((label) => label.page === figure.page);
	if (onPage.length === 0) {
		return [];
	}
	const positioned = onPage.filter((label): label is FigureLabel & { y: number } => label.y !== null);
	if (positioned.length === 0) {
		return onPage.map((label) => label.questionNumber);
	}
	const figureTop = figure.y + figure.height;
	const above = positioned.filter((label) => label.y >= figureTop - LABEL_SLACK);
	const pool = above.length > 0 ? above : positioned;
	const chosen = pool.reduce((nearest, label) => (label.y < nearest.y ? label : nearest));
	return [chosen.questionNumber];
};

/** Group figures by question. Each list runs from the top of the page downward. */
export const groupFiguresByQuestion = (
	figures: readonly ClusteredFigure[],
	labels: readonly FigureLabel[],
): Map<string, ClusteredFigure[]> => {
	const grouped = new Map<string, ClusteredFigure[]>();
	for (const figure of figures) {
		for (const questionNumber of questionNumbersFor(figure, labels)) {
			const list = grouped.get(questionNumber) ?? [];
			list.push(figure);
			grouped.set(questionNumber, list);
		}
	}
	for (const list of grouped.values()) {
		list.sort((left, right) => right.y - left.y || left.x - right.x);
	}
	return grouped;
};

const CAPTION_KIND: Record<string, string> = {
	image: "image",
	images: "image",
	delwedd: "image",
	delweddau: "image",
	llun: "image",
	lluniau: "image",
	graph: "graph",
	graphs: "graph",
	graff: "graph",
	graffiau: "graph",
	table: "table",
	tables: "table",
	tabl: "table",
	tablau: "table",
	diagram: "diagram",
	diagrams: "diagram",
	diagramau: "diagram",
	figure: "figure",
	figures: "figure",
	ffigur: "figure",
	ffigurau: "figure",
	chart: "chart",
	charts: "chart",
	siart: "chart",
	siartiau: "chart",
};

/** Caption words the exact-caption test accepts. The longest word is tried first. */
export const CAPTION_WORD_LIST: readonly string[] = Object.keys(CAPTION_KIND);

const CAPTION_WORDS = [...CAPTION_WORD_LIST].sort((left, right) => right.length - left.length).join("|");

const EXACT_CAPTION = new RegExp(`^(?:${CAPTION_WORDS})\\s+\\d+(?:\\.\\d+)?$`, "i");

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const captionPattern = (words: readonly string[]): RegExp => {
	const body = [...words]
		.map((word) => word.trim())
		.filter((word) => word.length > 0)
		.sort((left, right) => right.length - left.length)
		.map((word) => escapeRegExp(word))
		.join("|");
	return new RegExp(`^(?:${body})\\s+\\d+(?:\\.\\d+)?$`, "i");
};

/** The whole run is a caption, such as "Image 1.2" or "Figure 1". */
export const isExactAssetCaption = (text: string, words?: readonly string[]): boolean => {
	const source = text.trim();
	if (!words) {
		return EXACT_CAPTION.test(source);
	}
	const trimmed = words.map((word) => word.trim()).filter((word) => word.length > 0);
	if (trimmed.length === 0) {
		return false;
	}
	return captionPattern(trimmed).test(source);
};

const captionId = (word: string, number: string): string =>
	`${CAPTION_KIND[word.toLowerCase()] ?? word.toLowerCase()} ${number}`;

const captionIdsInText = (text: string): string[] => {
	const pattern = new RegExp(
		`\\b(${CAPTION_WORDS})\\s+(\\d+(?:\\.\\d+)?)(?:\\s+(?:and|a)\\s+(\\d+(?:\\.\\d+)?))?`,
		"gi",
	);
	const ids: string[] = [];
	for (const match of text.matchAll(pattern)) {
		const word = match[1];
		const first = match[2];
		const second = match[3];
		if (!word || !first) {
			continue;
		}
		ids.push(captionId(word, first));
		if (second) {
			ids.push(captionId(word, second));
		}
	}
	return ids;
};

const anchorCaptionId = (text: string): string | undefined => {
	const match = new RegExp(`^(${CAPTION_WORDS})\\s+(\\d+(?:\\.\\d+)?)\\b`, "i").exec(text.trim());
	if (!match?.[1] || !match[2]) {
		return undefined;
	}
	return captionId(match[1], match[2]);
};

const verticalGap = (figure: ClusteredFigure, y: number): number => {
	const top = figure.y + figure.height;
	if (y > top) {
		return y - top;
	}
	if (y < figure.y) {
		return figure.y - y;
	}
	return 0;
};

const captionIdsForFigure = (
	figure: ClusteredFigure,
	items: readonly { text: string; x: number; y: number }[],
): string[] => {
	const anchors: { id: string; x: number }[] = [];
	for (const item of items) {
		const id = anchorCaptionId(item.text);
		if (!id || verticalGap(figure, item.y) > CAPTION_SLACK) {
			continue;
		}
		anchors.push({ id, x: item.x });
	}
	const covering = anchors.filter(
		(anchor) => anchor.x >= figure.x - CAPTION_X_BEFORE && anchor.x <= figure.x + figure.width + CAPTION_X_AFTER,
	);
	const chosen =
		covering.length > 0
			? covering
			: anchors.length === 0
				? []
				: [
						anchors.reduce((nearest, anchor) =>
							Math.abs(anchor.x - figure.x) < Math.abs(nearest.x - figure.x) ? anchor : nearest,
						),
					];
	return [...new Set(chosen.map((anchor) => anchor.id))];
};

export interface PaperFigureQuestion {
	questionNumber: string;
	context?: readonly string[];
	text: string;
}

/**
 * Give a captioned figure to every question that names it.
 * A figure with no caption still uses the label above it.
 */
export const groupPaperFigures = (
	figures: readonly ClusteredFigure[],
	questions: readonly PaperFigureQuestion[],
	pages: readonly { pageNumber: number; items: readonly { text: string; x: number; y: number }[] }[],
	labels: readonly FigureLabel[],
): Map<string, ClusteredFigure[]> => {
	const questionsByCaption = new Map<string, string[]>();
	for (const question of questions) {
		const blob = [...(question.context ?? []), question.text].join(" ");
		for (const id of captionIdsInText(blob)) {
			const list = questionsByCaption.get(id) ?? [];
			if (!list.includes(question.questionNumber)) {
				list.push(question.questionNumber);
			}
			questionsByCaption.set(id, list);
		}
	}

	const grouped = new Map<string, ClusteredFigure[]>();
	const add = (questionNumber: string, figure: ClusteredFigure): void => {
		const list = grouped.get(questionNumber) ?? [];
		const isAlreadyStored = list.some(
			(item) => item.svg === figure.svg && item.page === figure.page && item.y === figure.y,
		);
		if (isAlreadyStored) {
			return;
		}
		list.push(figure);
		grouped.set(questionNumber, list);
	};

	for (const figure of figures) {
		const items = pages.find((page) => page.pageNumber === figure.page)?.items ?? [];
		const ids = captionIdsForFigure(figure, items);
		const named = [...new Set(ids.flatMap((id) => questionsByCaption.get(id) ?? []))];
		const targets = named.length > 0 ? named : questionNumbersFor(figure, labels);
		for (const questionNumber of targets) {
			add(questionNumber, figure);
		}
	}

	for (const list of grouped.values()) {
		list.sort((left, right) => right.y - left.y || left.x - right.x);
	}
	return grouped;
};

const indexKeys = (keys: ReadonlyMap<string, readonly string[]>): Map<string, readonly string[]> => {
	const indexed = new Map<string, readonly string[]>();
	for (const [questionNumber, values] of keys) {
		const key = normaliseAssetNumber(questionNumber);
		if (!indexed.has(key)) {
			indexed.set(key, values);
		}
	}
	return indexed;
};

const pairSlots = (english: readonly string[], welsh: readonly string[]): AssetRef[] => {
	const slots: AssetRef[] = [];
	const count = Math.max(english.length, welsh.length);
	for (let index = 0; index < count; index += 1) {
		const slot: AssetRef = {};
		if (english[index]) {
			slot.en = english[index];
		}
		if (welsh[index]) {
			slot.cy = welsh[index];
		}
		if (slot.en || slot.cy) {
			slots.push(slot);
		}
	}
	return slots;
};

/** Pair English and Welsh keys in figure order. The manifest omits a question with no asset. */
export const buildAssetManifest = (input: {
	order: readonly string[];
	paperEn: ReadonlyMap<string, readonly string[]>;
	paperCy: ReadonlyMap<string, readonly string[]>;
	markSchemeEn: ReadonlyMap<string, readonly string[]>;
	markSchemeCy: ReadonlyMap<string, readonly string[]>;
}): AssetManifest => {
	const paperEn = indexKeys(input.paperEn);
	const paperCy = indexKeys(input.paperCy);
	const markSchemeEn = indexKeys(input.markSchemeEn);
	const markSchemeCy = indexKeys(input.markSchemeCy);
	const seen = new Set<string>();
	const numbers: string[] = [];
	const add = (questionNumber: string) => {
		const key = normaliseAssetNumber(questionNumber);
		if (key.length === 0 || seen.has(key)) {
			return;
		}
		seen.add(key);
		numbers.push(questionNumber);
	};
	for (const questionNumber of input.order) {
		add(questionNumber);
	}
	for (const questionNumber of [
		...input.paperEn.keys(),
		...input.paperCy.keys(),
		...input.markSchemeEn.keys(),
		...input.markSchemeCy.keys(),
	]) {
		add(questionNumber);
	}

	const questions: AssetManifest["questions"] = [];
	for (const questionNumber of numbers) {
		const key = normaliseAssetNumber(questionNumber);
		const paper = pairSlots(paperEn.get(key) ?? [], paperCy.get(key) ?? []);
		const markScheme = pairSlots(markSchemeEn.get(key) ?? [], markSchemeCy.get(key) ?? []);
		if (paper.length === 0 && markScheme.length === 0) {
			continue;
		}
		questions.push({ questionNumber, paper, markScheme });
	}
	return { questions };
};

/**
 * Place each mark-scheme question on its extracted page.
 * A text item that is the question number supplies y. A missing item leaves y null.
 */
export const markSchemeFigureLabels = (
	questions: readonly { questionNumber: string; page: number }[],
	pages: readonly { pageNumber: number; items: readonly { text: string; y: number }[] }[],
): FigureLabel[] => {
	const original = new Map<string, string>();
	const labels = new Map<string, FigureLabel>();
	for (const question of questions) {
		const key = normaliseAssetNumber(question.questionNumber);
		if (!original.has(key)) {
			original.set(key, question.questionNumber);
		}
		const id = `${question.page}:${key}`;
		if (!labels.has(id)) {
			labels.set(id, { questionNumber: question.questionNumber, page: question.page, y: null });
		}
	}
	for (const page of pages) {
		for (const item of page.items) {
			const key = normaliseAssetNumber(item.text);
			const questionNumber = original.get(key);
			if (!questionNumber) {
				continue;
			}
			const id = `${page.pageNumber}:${key}`;
			const existing = labels.get(id);
			if (!existing || existing.y === null || item.y > existing.y) {
				labels.set(id, { questionNumber, page: page.pageNumber, y: item.y });
			}
		}
	}
	return [...labels.values()];
};
